import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { useLocalStorage, usePrevious } from 'react-use';
import AuthContext, { PermissionListType } from '../contexts/AuthContext';
import CommonPermissions from '#core/Permissions/components/CommonPermissions';
import { AUTH0_AUDIENCE, AUTH0_SCOPE } from '#config/auth0Config';
import { useAppLoading } from '#core/AppLoading';
import { useEnvironment } from '#core/Environment';
import getPermissionToken from '#utils/getPermissionToken';
import getPermissionList from '#utils/getPermissionList';

const Auth0Provider: React.FC<any> = ({ children: renderChildren }) => {
  const [auth0AccessToken, setAuth0AccessToken] = useState<string | null>(null);
  const [permissionToken, setPermissionToken] = useState(null);
  const [permissionList, setPermissionList] = useState<
    PermissionListType[] | null
  >(null);
  const {
    isAuthenticated,
    getAccessTokenSilently,
    user,
    logout,
    loginWithRedirect,
    isLoading,
  } = useAuth0();
  const { setIsLoading } = useAppLoading();
  const { apiEndpoint } = useEnvironment();
  const groups = useMemo(() => user?.groups || ([] as string[]), [
    user?.groups,
  ]);
  const roles = useMemo(
    () => user?.['https://timeshighereducation.com/roles'] || ([] as string[]),
    [user],
  );
  const canUseElevatedPermissions = roles.includes('data_submitter');

  const [useElevatedPermissions, setUseElevatedPermissions] = useLocalStorage<
    boolean
  >(`${user?.sub}-use-elevated-permissions`, canUseElevatedPermissions);
  const previousUseElevatedPermisisons = usePrevious(useElevatedPermissions);

  useEffect(() => {
    if (useElevatedPermissions !== previousUseElevatedPermisisons) {
      setIsLoading(true);
    }
  }, [useElevatedPermissions, previousUseElevatedPermisisons, setIsLoading]);

  // eslint-disable-next-line no-console
  console.log('Auth0 User:', user);

  useEffect(() => {
    const getAccessToken = async () => {
      try {
        const accessToken = await getAccessTokenSilently({
          // @ts-expect-error
          audience: AUTH0_AUDIENCE,
          scope: AUTH0_SCOPE,
        });

        setAuth0AccessToken(accessToken);
      } catch (e) {
        const error: any = e;
        // eslint-disable-next-line no-console
        console.log('Error: ', error.message);
      }
    };

    getAccessToken();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const getPermissionWrapper = async (authToken: string) => {
      const token = await getPermissionToken(apiEndpoint, authToken);
      const list = await getPermissionList(apiEndpoint, authToken);

      setPermissionToken(token);
      setPermissionList(list);
    };

    if (auth0AccessToken) {
      getPermissionWrapper(auth0AccessToken);
    }
  }, [auth0AccessToken, apiEndpoint]);

  const authContextValue: any = useMemo(
    () => ({
      isAuthenticated,
      token: auth0AccessToken,
      apiHeaders: { authorization: `Bearer ${auth0AccessToken}` },
      canUseElevatedPermissions,
      useElevatedPermissions:
        canUseElevatedPermissions && useElevatedPermissions === true,
      setUseElevatedPermissions: canUseElevatedPermissions
        ? setUseElevatedPermissions
        : () => {},
      actions: {
        logIn: loginWithRedirect || (() => Promise.resolve()),
        logOut: () => {
          const fn = logout || (() => Promise.resolve());
          sessionStorage.clear();
          return fn();
        },
      },
      user: {
        ...user,
        id: user?.sub,
      },
      roles,
      groups,
      permissionToken,
      permissionList,
    }),
    [
      isAuthenticated,
      auth0AccessToken,
      setUseElevatedPermissions,
      canUseElevatedPermissions,
      useElevatedPermissions,
      logout,
      loginWithRedirect,
      user,
      roles,
      groups,
      permissionToken,
      permissionList,
    ],
  );

  if (typeof isAuthenticated === 'undefined' || isLoading) {
    return null;
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      <CommonPermissions resourcePermissions={{}}>
        {renderChildren(authContextValue)}
      </CommonPermissions>
    </AuthContext.Provider>
  );
};

export default Auth0Provider;
